import { useFormik } from 'formik';
import { ChangeEvent, useEffect, useState } from 'react';
import usePatchVehicle from '../../../../API/hooks/vehicles/usePatchVehicle';
import { PatchVehicleRequest, VehicleDetails } from '../../../../API/types/Vehicles';
import Input from '../../../../components/common/Input/Input';
import { Button } from '../../../../components/ui/Button/Button';
import S from './EditVehicleStyles';
import useVehicleThumbnail from '../../../../theme/useVehicleThumbnail';
import {
  generatePreviewLicenseNumber,
  generatePreviewModel,
} from '../../../Vehicles/Modals/helpers';
import Select from '../../../../components/common/Select/Select';
import { getFormattedDateAndTime } from '../../../../utils/dateUtils';

type Props = {
  fleetId: string;
  vehicle?: VehicleDetails;
  toggleModal: (closed: boolean) => void;
};

function AddVehicle({ fleetId, vehicle, toggleModal }: Props): JSX.Element {
  const vehicleThumbnail = useVehicleThumbnail();
  const [submitError, setSubmitError] = useState<string | string[] | null>(null);
  const onSubmitSuccess = (): void => toggleModal(false);
  const onSubmitFail = (errorMessage: string): void => setSubmitError(errorMessage);

  // formik can't process async actions and work with server response,
  // thus we have to pass callbacks to tanstack mutation
  const { mutate } = usePatchVehicle(onSubmitSuccess, onSubmitFail);

  const formik = useFormik({
    initialValues: {
      id: vehicle?._id,
      licensePlateNumber: vehicle?.licensePlateNumber || '',
      manufacturer: vehicle?.manufacturer || '',
      model: vehicle?.model || '',
      color: vehicle?.color || '',
      category: vehicle?.category || 'car',
    },
    onSubmit: (values) => mutate({ ...values, fleetId } as unknown as PatchVehicleRequest),
    validate: (values) => {
      const errors = {} as Record<string, string | undefined>;

      if (!values.licensePlateNumber.length) errors.licensePlateNumber = 'Required';
      if (!values.manufacturer.length) errors.manufacturer = 'Required';
      if (!values.model.length) errors.model = 'Required';
      if (!values.color.length) errors.color = 'Required';
      if (!values.category.length) errors.category = 'Required';

      return errors;
    },
    validateOnMount: true,
  });

  // if there is onSubmit error formik will stack in submitting state
  // blocking further interactions with submit button
  useEffect(() => {
    formik.setSubmitting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitError]);

  const handleInputChange = (e: ChangeEvent<any>): void => {
    formik.handleChange(e);
    setSubmitError('');
  };

  const handleSelectChange = (e: ChangeEvent<any>): void => {
    formik.handleChange(e);
    setSubmitError('');
  };

  return (
    <S.Wrapper>
      <form onSubmit={formik.handleSubmit}>
        <div className="details-container">
          <h4 className="form-title"> Vehicle details </h4>
          <S.RowContainer>
            <Input
              label="Vehicle number"
              name="licensePlateNumber"
              placeholder="AA 12 B 1234"
              onChange={handleInputChange}
              onBlur={formik.handleBlur}
              value={formik.values.licensePlateNumber}
              error={formik.touched.licensePlateNumber && formik.errors.licensePlateNumber}
            />
          </S.RowContainer>
          <S.RowContainer>
            <Input
              label="Manufacturer"
              name="manufacturer"
              placeholder="Manufacturer"
              onChange={handleInputChange}
              onBlur={formik.handleBlur}
              value={formik.values.manufacturer}
              error={formik.touched.manufacturer && formik.errors.manufacturer}
            />
            <Input
              label="Model"
              name="model"
              placeholder="Model"
              onChange={handleInputChange}
              onBlur={formik.handleBlur}
              value={formik.values.model}
              error={formik.touched.model && formik.errors.model}
            />
            <Input
              label="Color"
              name="color"
              placeholder="Color"
              onChange={handleInputChange}
              onBlur={formik.handleBlur}
              value={formik.values.color}
              error={formik.touched.color && formik.errors.color}
            />
          </S.RowContainer>
          <S.RowContainer>
            <Select
              value={formik.values.category}
              name="category"
              label="Select class of vehicle:"
              options={[
                {
                  label: 'Car',
                  value: 'car',
                },
                {
                  label: 'Minibus',
                  value: 'minibus',
                },
                {
                  label: 'Truck',
                  value: 'truck',
                },
              ]}
              onChange={handleSelectChange}
            />
          </S.RowContainer>
          <S.RowContainer>
            <h4 className="vehicle-info">
              Vehicle was added at {vehicle?.createdAt ? getFormattedDateAndTime(vehicle?.createdAt) : 'N/A'}. Last update: {vehicle?.updatedAt ? getFormattedDateAndTime(vehicle.updatedAt) : 'N/A'}
            </h4>
          </S.RowContainer>
          {!!submitError && (
            typeof submitError === 'string'
              ? <S.ErrorMessage>{submitError}</S.ErrorMessage>
              : submitError.map((error) => <S.ErrorMessage>{error}</S.ErrorMessage>)
          )}
        </div>
        <div className="preview-container">
          <div className="preview-card">
            <h4 className="preview-card-title"> Vehicle preview </h4>
            <div className="preview-card-tags-container">
              <img src={vehicleThumbnail} alt="vehicle" />
              <p className="license">
                {generatePreviewLicenseNumber(formik.values.licensePlateNumber)}
              </p>
              <p className="model">
                {generatePreviewModel(formik.values.model)}
              </p>
            </div>
            <div className="preview-card-buttons-container">
              <Button
                height="50px"
                onClick={() => toggleModal(false)}
                type="button"
              >
                Cancel
              </Button>
              <Button
                className="add-button"
                height="50px"
                type="submit"
                disabled={!formik.dirty || formik.isSubmitting || !formik.isValid || !!submitError}
              >
                Update
              </Button>
            </div>
          </div>
        </div>
      </form>
    </S.Wrapper>
  );
}

export default AddVehicle;
